import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html } from "lit";
import { property, state } from "lit/decorators.js";
import { pdIcons } from "@progressive-development/pd-icon";
import "@progressive-development/pd-icon/pd-icon";
//#region src/pd-collapse/PdCollapse.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
/**
* Collapsible accordion component with header and content.
*
* @tagname pd-collapse
* @summary Collapsible accordion section with animated toggle.
*
* @event pd-collapse-toggle - Fired when toggle state changes. Emits `{active, name}`.
* @event pd-collapse-register - Fired on mount for container registration.
* @event pd-collapse-unregister - Fired on unmount for container cleanup.
*
* @slot header - Visible header area content.
* @slot content - Collapsible content area.
*
* @csspart trigger - Die Kopfzeile (Klickfläche mit Titel und Zeichen).
* @csspart content - Die Fläche des Inhalts: Rahmen, Hintergrund, Polster.
*   Bewusst NICHT der Animationscontainer darüber — dessen Höhe, Transform und
*   Sichtbarkeit gehören der Komponente.
*
* @cssprop --pd-collapse-border-radius - Border radius. Default: `--pd-radius-md`.
* @cssprop --pd-collapse-header-font-size - Header font size.
* @cssprop --pd-collapse-font-col - Header text color.
* @cssprop --pd-collapse-bg-col - Header background.
* @cssprop --pd-collapse-hover-col - Header hover background.
* @cssprop --pd-collapse-hover-font-col - Header hover font color
* @cssprop --pd-collapse-icon-col - Toggle icon color. Default: inherits from `--pd-collapse-font-col`.
* @cssprop --pd-collapse-icon-col-hover - Toggle icon hover color. Default: inherits from `--pd-collapse-font-col`.
* @cssprop --pd-collapse-content-bg-col - Content background.
* @cssprop --pd-collapse-content-border-col - Content border color.
* @cssprop --pd-collapse-content-padding - Content padding. Default: `--pd-spacing-sm`.
* @cssprop --pd-collapse-header-shadow - Header box-shadow for depth effect. Default: subtle shadow.
*/
var PdCollapse = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.icon = pdIcons.ICON_TOGGLE_COLLAPSE;
		this.startOpen = false;
		this.scrollOnOpen = false;
		this._active = false;
		this._settled = false;
	}
	static {
		this.styles = [css`
      :host {
        --_radius: var(--pd-collapse-border-radius, var(--pd-radius-md));

        display: block;
        width: 100%;
        position: relative;
        overflow: visible;
      }

      .trigger {
        position: relative;
        z-index: 2;

        display: flex;
        justify-content: space-between;
        align-items: center;
        box-sizing: border-box;
        padding: var(--pd-spacing-sm) var(--pd-spacing-md);
        cursor: pointer;

        font-family: var(--pd-default-font-title-family);
        font-size: var(--pd-collapse-header-font-size, 1.1rem);
        font-weight: bold;
        color: var(--pd-collapse-font-col, var(--pd-default-bg-col));
        background: var(--pd-collapse-bg-col, var(--pd-default-col));
        border-radius: var(--_radius) var(--_radius) 0 0;
        box-shadow: var(
          --pd-collapse-header-shadow,
          0 2px 4px rgba(0, 0, 0, 0.1)
        );

        transition-property: box-shadow, background;
        transition: 0.2s ease-in;
      }

      .trigger pd-icon {
        /* Primary icon uses --pd-icon-primary-col and --pd-icon-col-primary-active */
        --pd-icon-primary-col: var(
          --pd-collapse-icon-col,
          var(--pd-collapse-font-col, var(--pd-default-bg-col))
        );
        --pd-icon-col-primary-active: var(
          --pd-collapse-icon-col,
          var(--pd-collapse-font-col, var(--pd-default-bg-col))
        );
        --pd-icon-col-hover: var(
          --pd-collapse-icon-col-hover,
          var(--pd-default-hover-col)
        );
      }

      .trigger:hover {
        background: var(--pd-collapse-hover-col, var(--pd-default-dark-col));
        color: var(--pd-collapse-hover-font-col, var(--pd-default-bg-col));
      }

      .trigger:focus-visible {
        outline: 2px solid var(--pd-focus-ring-col);
        outline-offset: 2px;
      }

      .trigger.closed {
        border-radius: var(--_radius);
      }

      ::slotted(.header) {
        display: flex;
        align-items: center;
      }

      /* Trägt die MECHANIK des Auf- und Zuklappens: Höhe, Sichtbarkeit,
       * Transform, Opazität. Bewusst ohne Dekoration — die liegt eine Ebene
       * tiefer in .surface, damit ein Styling über den content-Part von außen
       * die Animation nicht anfassen kann. */
      .content {
        position: relative;
        z-index: 1;

        height: 0;
        visibility: hidden;
        overflow: hidden;
        opacity: 0;
        box-sizing: border-box;

        border-radius: 0 0 var(--_radius) var(--_radius);

        will-change: transform, opacity;
        transform: translate3d(0, -1.5em, 0);
        pointer-events: none;

        transform-origin: top;
        transition:
          transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
          opacity 0.3s ease,
          visibility 0s linear 0.3s; /* sichtbar wird später deaktiviert */
      }

      /* Die FLÄCHE des Inhalts: Rahmen, Hintergrund, Polster. Sie trägt den
       * content-Part — wer mehr will als die Tokens hergeben (etwa einen
       * breiten Rahmen mit Verlauf), gestaltet hier und lässt die Mechanik
       * darüber unberührt. Die Token-Defaults bleiben, wer nichts tut, sieht
       * nichts Neues. */
      .surface {
        box-sizing: border-box;

        border: 1px solid
          var(--pd-collapse-content-border-col, var(--pd-default-light-col));
        border-top: none;
        background: var(--pd-collapse-content-bg-col, var(--pd-default-bg-col));
        border-radius: 0 0 var(--_radius) var(--_radius);
        padding: var(--pd-collapse-content-padding, var(--pd-spacing-sm));
      }

      .content.open {
        height: auto;
        visibility: visible;
        /* Nur visible, KEIN overflow-y: auto. Sobald eine Achse auto ist,
         * berechnet CSS die andere ebenfalls zu auto — der offene Bereich
         * wird dann ein Scrollport und schneidet alles ab, was übersteht:
         * Schatten am Inhalt, aufklappende Menüs, Tooltips. Gescrollt hat er
         * ohnehin nie, weil die Höhe auto ist. Dieselbe Sorte Falle wie beim
         * transform im Ruhezustand weiter unten. */
        overflow: visible;
        opacity: 1;

        transform: translate3d(0, 0, 0);
        pointer-events: auto;

        transition:
          transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
          opacity 0.3s ease,
          visibility 0s linear 0s; /* sichtbar wird sofort gesetzt */
      }

      /* Ruhezustand nach abgeschlossener Öffnungs-Animation: Transform und
       * will-change entfernen. Ein permanentes transform ≠ none würde
       * .content zum Containing Block für position:fixed-Nachfahren machen
       * (Modals/Lightboxen im Slot blieben in der Box gefangen) und hält
       * grundlos eine Compositing-Layer. */
      .content.open.settled {
        transform: none;
        will-change: auto;
      }
    `];
	}
	firstUpdated() {
		this._registerAtContainer();
		if (this._active) this._settled = true;
	}
	disconnectedCallback() {
		this._unRegisterAtContainer();
		super.disconnectedCallback();
	}
	willUpdate(changedProps) {
		if (changedProps.has("startOpen")) {
			this._active = this.startOpen;
			if (!this.startOpen) this._settled = false;
		}
	}
	render() {
		return html`
      <div
        class="trigger ${this._active ? "open" : "closed"}"
        part="trigger"
        role="button"
        tabindex="0"
        aria-expanded="${this._active}"
        aria-controls="${this._contentId}"
        @click=${this._onClick}
        @keydown=${this._onKeyDown}
      >
        <slot name="header"></slot>
        <pd-icon
          icon="${this.icon}"
          ?activeIcon="${this._active}"
          class="small primary"
          aria-hidden="true"
        ></pd-icon>
      </div>
      <div
        id="${this._contentId}"
        class="content ${this._active ? "open" : "closed"} ${this._settled ? "settled" : ""}"
        role="region"
        aria-hidden="${!this._active}"
        @transitionend=${this._onContentTransitionEnd}
      >
        <div class="surface" part="content">
          <slot name="content"></slot>
        </div>
      </div>
    `;
	}
	/** @ignore */
	_onContentTransitionEnd(e) {
		if (e.target === e.currentTarget && e.propertyName === "transform" && this._active) this._settled = true;
	}
	/**
	* Öffnet den Collapse-Bereich
	*/
	set active(activeVal) {
		const oldActive = this._active;
		this._active = activeVal;
		if (oldActive !== activeVal) {
			if (!activeVal) this._settled = false;
			this.dispatchEvent(new CustomEvent("pd-collapse-toggle", {
				bubbles: true,
				composed: true,
				detail: {
					active: this._active,
					name: this.id || this._getInternalId()
				}
			}));
			if (this._active && this.scrollOnOpen) this._scrollContentIntoView();
		}
	}
	get active() {
		return this._active;
	}
	_onClick() {
		this.active = !this._active;
	}
	_onKeyDown(e) {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			this._onClick();
		}
	}
	/** @ignore */
	get _contentId() {
		return `${this.id || this._getInternalId()}-content`;
	}
	/** @ignore */
	_scrollContentIntoView() {
		setTimeout(() => {
			(this.shadowRoot?.getElementById(this._contentId))?.scrollIntoView({
				behavior: "smooth",
				block: "nearest"
			});
		}, 350);
	}
	_registerAtContainer() {
		this.dispatchEvent(new CustomEvent("pd-collapse-register", {
			detail: {
				name: this.id || this._getInternalId(),
				collapse: this
			},
			bubbles: true,
			composed: true
		}));
	}
	_unRegisterAtContainer() {
		document.dispatchEvent(new CustomEvent("pd-collapse-unregister", {
			detail: { name: this.id || this._getInternalId() },
			bubbles: true,
			composed: true
		}));
	}
	_getInternalId() {
		if (!this._internalId) this._internalId = `id${(/* @__PURE__ */ new Date()).getMilliseconds()}_${Math.random()}`;
		return this._internalId;
	}
};
__decorate([property({ type: String })], PdCollapse.prototype, "icon", void 0);
__decorate([property({ type: Boolean })], PdCollapse.prototype, "startOpen", void 0);
__decorate([property({ type: Boolean })], PdCollapse.prototype, "scrollOnOpen", void 0);
__decorate([state()], PdCollapse.prototype, "_active", void 0);
__decorate([state()], PdCollapse.prototype, "_settled", void 0);
//#endregion
export { PdCollapse };
